import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import ApiService from '../../services/api.service';
import { formatDate } from '../../utils/dateFormat';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { ProTableStats } from '../../components/pro-table';
import PortalTaskDetail from './PortalTaskDetail';

// Same toolbar as the typing-center pages: one 32px row.
const TOOLBAR_SX = {
  '& .MuiInputBase-root': { height: 32 },
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};
const MENU_ITEM_SX = { fontSize: '0.8125rem' };

const STATUS_LABELS = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed'
};

const STATUS_COLORS = {
  PENDING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success'
};

const STAT_STATUSES = ['', 'PENDING', 'IN_PROGRESS', 'COMPLETED'];

const PortalTasks = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [statusCounts, setStatusCounts] = useState(null);

  const [workOrders, setWorkOrders] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [workOrderFilter, setWorkOrderFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(searchTerm);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, size: rowsPerPage };
      if (statusFilter) params.status = statusFilter;
      if (workOrderFilter) params.workOrderId = workOrderFilter;
      if (employeeFilter) params.customerName = employeeFilter;
      if (appliedSearch) params.search = appliedSearch;

      const response = await ApiService.getPortalTasks(params);
      const pageData = response.data?.data || {};
      setTasks(Array.isArray(pageData.content) ? pageData.content : []);
      setTotalTasks(pageData.total_elements ?? pageData.totalElements ?? 0);
    } catch (err) {
      setTasks([]);
      setTotalTasks(0);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, workOrderFilter, employeeFilter, appliedSearch]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Tile counts: the list is paged on the server, so ask it for one row per status and read the total.
  const fetchStatusCounts = useCallback(async () => {
    try {
      const results = await Promise.all(STAT_STATUSES.map((status) =>
        ApiService.getPortalTasks({ page: 0, size: 1, ...(status ? { status } : {}) })
      ));
      const counts = {};
      results.forEach((res, i) => {
        const pageData = res.data?.data || {};
        counts[STAT_STATUSES[i]] = pageData.total_elements ?? pageData.totalElements ?? 0;
      });
      setStatusCounts(counts);
    } catch (err) {
      setStatusCounts({});
    }
  }, []);

  useEffect(() => { fetchStatusCounts(); }, [fetchStatusCounts]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [woResponse, empResponse] = await Promise.all([
          ApiService.getPortalWorkOrders(),
          ApiService.getPortalTaskEmployees()
        ]);
        setWorkOrders(woResponse.data?.data || []);
        setEmployees(empResponse.data?.data || []);
      } catch (err) {
        setWorkOrders([]);
        setEmployees([]);
      }
    };
    loadFilterOptions();
  }, []);

  const openDetail = (taskId) => {
    setSelectedTaskId(taskId);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedTaskId(null);
  };

  const onFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const refresh = () => {
    fetchTasks();
    fetchStatusCounts();
  };

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button size="small" onClick={fetchTasks} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <ProTableStats
        loading={!statusCounts}
        cards={[
          { key: '', label: 'Total', value: statusCounts?.[''] ?? 0 },
          { key: 'PENDING', label: 'Pending', value: statusCounts?.PENDING ?? 0, color: 'warning.main' },
          { key: 'IN_PROGRESS', label: 'In Progress', value: statusCounts?.IN_PROGRESS ?? 0, color: 'info.main' },
          { key: 'COMPLETED', label: 'Completed', value: statusCounts?.COMPLETED ?? 0, color: 'success.main' },
        ].map((c) => ({
          ...c,
          highlight: statusFilter === c.key,
          onClick: () => { setStatusFilter(c.key); setPage(0); },
        }))}
      />

      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5, ...TOOLBAR_SX }}>
        <TextField
          size="small"
          placeholder="Search tasks..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: '1 1 200px', minWidth: 200, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </InputAdornment>
            )
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Employee</InputLabel>
          <Select value={employeeFilter} label="Employee" onChange={onFilterChange(setEmployeeFilter)}>
            <MenuItem value="" sx={MENU_ITEM_SX}>All</MenuItem>
            {employees.map((name) => (
              <MenuItem key={name} value={name} sx={MENU_ITEM_SX}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Work Order</InputLabel>
          <Select value={workOrderFilter} label="Work Order" onChange={onFilterChange(setWorkOrderFilter)}>
            <MenuItem value="" sx={MENU_ITEM_SX}>All</MenuItem>
            {workOrders.map((wo) => (
              <MenuItem key={wo.id} value={wo.id} sx={MENU_ITEM_SX}>
                {wo.workOrderNumber || wo.work_order_number || wo.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={onFilterChange(setStatusFilter)}>
            <MenuItem value="" sx={MENU_ITEM_SX}>All</MenuItem>
            <MenuItem value="PENDING" sx={MENU_ITEM_SX}>Pending</MenuItem>
            <MenuItem value="IN_PROGRESS" sx={MENU_ITEM_SX}>In Progress</MenuItem>
            <MenuItem value="COMPLETED" sx={MENU_ITEM_SX}>Completed</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {totalTasks} tasks
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={refresh} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead sx={getBrandedTableHeadSx(theme)}>
              <TableRow>
                <TableCell>Task #</TableCell>
                <TableCell>Work Order #</TableCell>
                <TableCell>Task Name</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Completed</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : tasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No tasks found
                  </TableCell>
                </TableRow>
              ) : (
                tasks.map((task) => (
                  <TableRow key={task.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetail(task.id)}>
                    <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{task.task_number || '—'}</TableCell>
                    <TableCell>{task.work_order_number || '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{task.task_name || '—'}</TableCell>
                    <TableCell>{task.customer_name || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={STATUS_LABELS[task.status] || task.status}
                        color={STATUS_COLORS[task.status] || 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(task.created_at, '—')}</TableCell>
                    <TableCell>{formatDate(task.completed_at, '—')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Task">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(task.id); }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalTasks}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>

      <PortalTaskDetail open={detailOpen} taskId={selectedTaskId} onClose={closeDetail} />
    </Box>
  );
};

export default PortalTasks;
